import { Button, Callout, Checkbox, Intent } from '@blueprintjs/core';
import { ModelTuned } from '@carbon/icons-react';
import * as React from 'react';
import { Text, Tooltip } from 'vet-bones/components';

import { EditableLabel } from 'src/components/EditableLabel';
import { TEST_IDS } from 'src/constants/testIds';
import { useProjectModelSettings } from 'src/containers/ProjectSettingsDrawer/hooks/useProjectModelSettingsValue';
import { useProjectSettingsValidation } from 'src/containers/ProjectSettingsDrawer/hooks/useProjectSettingsValidationValue';
import { projectModelsByIdSelector } from 'src/selectors/projectModelsById';
import * as copy from 'src/templates/copy';
import { useIsVoiceboxEnabled } from 'src/utils/hooks/useIsVoiceboxEnabled';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ProjectSettingsDrawerTableRowProps = {
  index: number;
  modelId: string;
  onOpenAiRules: (modelId: string) => void;
};

export const ProjectSettingsDrawerPrefixTableRow: React.VFC<
  ProjectSettingsDrawerTableRowProps
> = ({ index, modelId, onOpenAiRules }) => {
  const { projectId } = useRouterParams();
  const modelsById = useReduxSelector(projectModelsByIdSelector(projectId));

  const isVoiceboxEnabled = useIsVoiceboxEnabled();

  const { validation, withValidation } = useProjectSettingsValidation();

  const {
    modelNamespaces,
    modelPrefixes,
    shouldPublishPrefixByModel,
    voiceboxInstructionsByModel,
    setModelNamespaces,
    setModelPrefixes,
    setShouldPublishPrefixByModel,
  } = useProjectModelSettings();

  const onSetNamespace = (namespace: string) => {
    setModelNamespaces({
      ...modelNamespaces,
      [modelId]: namespace,
    });
  };
  const onSetPrefix = (prefix: string) => {
    setModelPrefixes({
      ...modelPrefixes,
      [modelId]: prefix,
    });
  };
  const onSetShouldPublishPrefixByModel = (shouldPublishPrefix: boolean) => {
    setShouldPublishPrefixByModel({
      ...shouldPublishPrefixByModel,
      [modelId]: shouldPublishPrefix,
    });
  };

  const isNamespaceValid = validation.modelNamespaces[modelId];
  const isPrefixValid = validation.modelPrefixes[modelId];

  const modelName = modelsById[modelId].name;
  const namespace = modelNamespaces[modelId];
  const shouldPublishPrefix = shouldPublishPrefixByModel[modelId];
  const prefix = modelPrefixes[modelId];
  const voiceboxInstructions = voiceboxInstructionsByModel[modelId];

  return (
    <tr
      className="sd-drawer-project-settings__prefix-table-row"
      data-testid={TEST_IDS.projectSettingsDrawer.modelNamespaceRow(
        modelId,
        index
      )}
    >
      <td>
        <Text>{modelName}</Text>
      </td>
      <td>
        <Callout
          className="sd-drawer-project-settings__prefix-table-row-prefix"
          icon={null}
          intent={!isNamespaceValid ? Intent.DANGER : Intent.NONE}
        >
          <EditableLabel
            errorIntent={Intent.DANGER}
            onSetValue={onSetNamespace}
            onValidate={withValidation('modelNamespaces', index)}
            placeholder={
              copy.components.projectSettingsDrawer.prefixTable
                .namespacePlaceholder
            }
            setValueOnChange
            value={namespace}
          />
        </Callout>
      </td>
      <td>
        <Checkbox
          checked={shouldPublishPrefix}
          onChange={() => {
            onSetShouldPublishPrefixByModel(!shouldPublishPrefix);
          }}
        />
      </td>
      <td>
        <Callout
          className="sd-drawer-project-settings__prefix-table-row-prefix"
          icon={null}
          intent={!isPrefixValid ? Intent.DANGER : Intent.NONE}
        >
          <EditableLabel
            errorIntent={Intent.DANGER}
            onSetValue={onSetPrefix}
            onValidate={withValidation('modelPrefixes', index)}
            placeholder={
              copy.components.projectSettingsDrawer.prefixTable
                .prefixPlaceholder
            }
            setValueOnChange
            value={prefix}
          />
        </Callout>
      </td>
      {isVoiceboxEnabled ? (
        <td>
          <Tooltip
            content={copy.components.projectSettingsDrawer.aiHints.tooltip}
          >
            <Button
              data-testid={TEST_IDS.projectSettingsDrawer.aiRulesButton(
                modelId
              )}
              icon={<ModelTuned />}
              intent={voiceboxInstructions ? Intent.PRIMARY : Intent.NONE}
              minimal
              onClick={() => onOpenAiRules(modelId)}
            />
          </Tooltip>
        </td>
      ) : null}
    </tr>
  );
};
